import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import AnalysisTemplateConfigContext from 'procedures/design/analysis-template-config/AnalysisTemplateConfigContext';
import {
  AnalysisInputs,
  AnalysisSourceRow,
  AnalysisSourceRowData,
  DatasetType,
  isLabAutoExecutionDatasetInfo,
  PlateDatasetType,
  StepOrDocumentAnalysisSources,
  AnalysisSourcesOrigin,
} from 'procedures/design/ProcedureMethodAnalysisTypes';
import {ProcedureParameterType} from 'procedures/ProcedureDomainTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import ScrollableTable from 'ui-library/scrollable/ScrollableTable';
import {
  RowSelectionMode,
  ScrollableTableColumn,
  TABLE_KEYS,
  TABLE_ROW_BODY_HEIGHT,
} from 'ui-library/scrollable/ScrollableTableConstants';

import {
  getConditionsDatasetRow,
  getLabAutoDatasetRows,
  getParameterDatasetRows,
  getPlateRecordAndDesignRows,
  getPlateRows,
  getSchemaSourceRows,
  getTestOrdersDatasetRows,
} from './getProcedureMethodAnalysisSourceTableRows';
import {
  getPlateDesignSourceIdentifier,
  getPlateRecordSourceIdentifier,
  getPlateSourceIdentifier,
  getSelectedIdsFromSelectedSources,
} from './procedureMethodAnalysisUtils';

// Each property corresponds to a DatasetType. Keys corresponding to lists of ids must be kept in sync with
// the dataset types specified in
// client/procedures/shared-components/analysis/procedureMethodAnalysisUtils:ID_BASED_DATASET_TYPES
export interface SelectedSources {
  conditions: boolean;
  dataParameters: boolean;
  materialInputs: boolean;
  materialOutputs: boolean;
  equipment: boolean;
  testOrders: boolean;
  previousTestOrders: boolean;
  plateSchemaIds: Array<string>;
  resultSchemaIds: Array<string>;
  entitySchemaIds: Array<string>;
  labAutoDatasetIds: Array<string>;
  // The IDs generated from parsePlateSourceIdentifier, not the raw plate API IDs
  positionsPlateSourceIds: Array<string>;
  // The IDs generated from parsePlateSourceIdentifier, not the raw plate API IDs
  contentsPlateSourceIds: Array<string>;
  positionsPlateRecordIds: Array<string>;
  contentsPlateRecordIds: Array<string>;
  positionsPlateDesignIds: Array<string>;
  contentsPlateDesignIds: Array<string>;
}

interface BaseAnalysisSourceTableProps {
  // Used to conditionally render the data based on the place where the data sources will be displayed.
  analysisSourcesOrigin: AnalysisSourcesOrigin;
  availableSourcesPerStep: Array<StepOrDocumentAnalysisSources>;
  // Used to determined whether parameter datasets would be non-empty, in case parameter data sources in
  // documents were deleted.
  availableParameterTypes: Array<ProcedureParameterType>;
  // Configures whether data sources that have the potential to generate data but would not in their current
  // state should be presented. An "empty data source" would be an unsubmitted structured table or lab auto
  // run.
  includeEmptyDataSources: boolean;
  /* Callback to determine whether a given column should be omitted. By default, all columns are shown. */
  isColumnHidden?: (accessorKey: keyof AnalysisSourceRowData) => boolean;

  // Callback used to navigate to the tests and dilutions page in configuration. When the user clicks on the
  // tests link for a test order dataset, this callback is invoked.
  navigateToTests?: () => void;
}

export interface ProcedureMethodAnalysisSourceTableProps extends BaseAnalysisSourceTableProps {
  isSelectorHidden: false;
  multiSelect: boolean;
  setSelectedSources: (selectedSources: SelectedSources) => void;
  selectedSources: SelectedSources;
}

/* Props leveraged for config view-only mode of table - all checkboxes hidden & no interactions supported. */
export interface ProcedureMethodAnalysisSourceTableWithoutSelectionProps extends BaseAnalysisSourceTableProps {
  isSelectorHidden: true;
  setSelectedSources?: never;
  selectedSources?: never;
}

const ALL_COLUMNS: Array<ScrollableTableColumn & {accessorKey: keyof AnalysisSourceRowData}> = [
  {header: 'Dataset', accessorKey: 'dataset', width: 300},
  {header: 'Data sources', accessorKey: 'dataSources', width: 400},
  {header: 'Creation time', accessorKey: 'creationTime', width: 170},
  {header: 'Steps', accessorKey: 'steps', width: 375, flexGrow: 1},
  {header: 'Used in', accessorKey: 'usedInAnalysis', width: 375},
];

export default function ProcedureMethodAnalysisSourceTable(
  props: ProcedureMethodAnalysisSourceTableProps | ProcedureMethodAnalysisSourceTableWithoutSelectionProps
): JSX.Element {
  const rowsWithoutSelectionInfo = useRef([
    ...(BenchlingConfig.SAMPLE_TESTS_ENABLE
      ? getTestOrdersDatasetRows(
          props.analysisSourcesOrigin,
          props.availableSourcesPerStep,
          props.navigateToTests
        )
      : []),
    ...getPlateRows(props.availableSourcesPerStep),
    ...getLabAutoDatasetRows(props.availableSourcesPerStep),
    getConditionsDatasetRow(),
    ...getParameterDatasetRows(
      props.availableSourcesPerStep,
      props.availableParameterTypes,
      props.includeEmptyDataSources
    ),
    // entity schema rows
    ...getSchemaSourceRows(
      props.availableSourcesPerStep,
      (analysisInputs) => analysisInputs.registrationTables,
      DatasetType.ENTITY_SCHEMA,
      props.includeEmptyDataSources
    ),
    // result schema rows
    ...getSchemaSourceRows(
      props.availableSourcesPerStep,
      (analysisInputs) => analysisInputs.resultTables,
      DatasetType.RESULT_SCHEMA,
      props.includeEmptyDataSources
    ),
    ...getPlateRecordAndDesignRows(props.availableSourcesPerStep),
  ]);

  const {usedInWhichAnalysisTemplates} = AnalysisTemplateConfigContext.useContext();

  // While we initialize the selection based on the provided prop. But after that, we manage selection state internally
  const [selectedRowIds, setSelectedRowIds] = useState<Array<string>>(
    props.selectedSources ? getSelectedIdsFromSelectedSources(props.selectedSources) : []
  );

  const handleRowsSelect = useCallback(
    (rows: Array<AnalysisSourceRow>, isSelected: boolean): void => {
      assert(!props.isSelectorHidden, 'Selection should be enabled');
      const touchedRowIds = rows.map((row) => row.id);

      let newSelection: Array<string>;
      if (isSelected) {
        newSelection = props.multiSelect ? [...selectedRowIds, ...touchedRowIds] : touchedRowIds;
      } else {
        newSelection = props.multiSelect
          ? selectedRowIds.filter((rowId) => !touchedRowIds.includes(rowId))
          : [];
      }
      setSelectedRowIds(newSelection);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRowIds]
  );

  // When the selection changes, re-calculate the selected sources and alert the caller
  useEffect(() => {
    const getSelectedIds = (
      getSourceIds: (analysisInputs: AnalysisInputs) => Array<string>
    ): Array<string> => {
      const allIdsOfType = props.availableSourcesPerStep.flatMap((stepSources) =>
        getSourceIds(stepSources.analysisInputs)
      );
      // De-duplicate because multiple sources can have the same source ID
      // For example, we can have multiple registration tables with the same registration schema
      return [...new Set(allIdsOfType.filter((id) => selectedRowIds.includes(id)))];
    };

    const getIsDatasetSelected = (datasetType: DatasetType): boolean => selectedRowIds.includes(datasetType);

    props.setSelectedSources?.({
      conditions: getIsDatasetSelected(DatasetType.CONDITIONS),
      dataParameters: getIsDatasetSelected(DatasetType.DATA_PARAMETER),
      materialInputs: getIsDatasetSelected(DatasetType.MATERIAL_INPUT),
      materialOutputs: getIsDatasetSelected(DatasetType.MATERIAL_OUTPUT),
      equipment: getIsDatasetSelected(DatasetType.EQUIPMENT),
      testOrders: getIsDatasetSelected(DatasetType.TEST_ORDERS),
      previousTestOrders: getIsDatasetSelected(DatasetType.PREVIOUS_TEST_ORDERS),
      plateSchemaIds: getSelectedIds((analysisInputs) =>
        analysisInputs.plates.map((plate) => plate.plateSchemaId)
      ),
      resultSchemaIds: getSelectedIds((analysisInputs) =>
        analysisInputs.resultTables.map((table) => table.schemaId)
      ),
      entitySchemaIds: getSelectedIds((analysisInputs) =>
        analysisInputs.registrationTables.map((table) => table.schemaId)
      ),
      labAutoDatasetIds: getSelectedIds((analysisInputs) =>
        analysisInputs.laRuns.flatMap((run) =>
          run.outputs.map((output) =>
            isLabAutoExecutionDatasetInfo(output) ? output.datasetId : output.outputFileConfigId
          )
        )
      ),
      positionsPlateSourceIds: getSelectedIds((analysisInputs) =>
        analysisInputs.plates.map(({plateId}) =>
          getPlateSourceIdentifier(plateId, PlateDatasetType.POSITIONS)
        )
      ),
      contentsPlateSourceIds: getSelectedIds((analysisInputs) =>
        analysisInputs.plates.map(({plateId}) => getPlateSourceIdentifier(plateId, PlateDatasetType.CONTENTS))
      ),
      positionsPlateRecordIds: getSelectedIds((analysisInputs) =>
        analysisInputs.plateRecords.map((plateRecord) =>
          getPlateRecordSourceIdentifier(plateRecord, PlateDatasetType.POSITIONS)
        )
      ),
      contentsPlateRecordIds: getSelectedIds((analysisInputs) =>
        analysisInputs.plateRecords.map((plateRecord) =>
          getPlateRecordSourceIdentifier(plateRecord, PlateDatasetType.CONTENTS)
        )
      ),
      positionsPlateDesignIds: getSelectedIds((analysisInputs) =>
        analysisInputs.plateDesigns.map((plateDesign) =>
          getPlateDesignSourceIdentifier(plateDesign, PlateDatasetType.POSITIONS)
        )
      ),
      contentsPlateDesignIds: getSelectedIds((analysisInputs) =>
        analysisInputs.plateDesigns.map((plateDesign) =>
          getPlateDesignSourceIdentifier(plateDesign, PlateDatasetType.CONTENTS)
        )
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowIds]);

  const rows = useMemo(
    () =>
      rowsWithoutSelectionInfo.current.map((r) => ({
        ...r,
        isSelected: selectedRowIds.includes(r.id),
      })),
    [selectedRowIds]
  );

  const getRowContentHeight = useCallback(
    (rowIdx: number): number => rows[rowIdx].data.height ?? TABLE_ROW_BODY_HEIGHT,
    [rows]
  );

  const usedInAnalysisByRowIndex = rows.map((row) => usedInWhichAnalysisTemplates(row.id));
  const areAllUsedInValuesNull = usedInAnalysisByRowIndex.every((usedIn) => usedIn == null);
  const shownColumns = ALL_COLUMNS.filter(({accessorKey}) => !props.isColumnHidden?.(accessorKey)).filter(
    ({accessorKey}) => !(accessorKey === 'usedInAnalysis' && areAllUsedInValuesNull)
  );

  return (
    <div {...getTestProps({component: 'ProcedureMethodAnalysisSourceTable', element: 'root'})}>
      <ScrollableTable
        rowSelectionMode={
          props.isSelectorHidden
            ? null
            : props.multiSelect
              ? RowSelectionMode.MULTIPLE
              : RowSelectionMode.SINGLE
        }
        disableSelection={false}
        columns={shownColumns}
        rows={rows.map((row, index) => ({
          ...row,
          data: {
            ...row.data,
            usedInAnalysis: usedInAnalysisByRowIndex[index],
          },
        }))}
        tableKey={TABLE_KEYS.PROCEDURE_METHOD_ANALYSIS_SOURCE_TABLE}
        canResizeColumns={false}
        onRowsSelect={handleRowsSelect}
        getRowContentHeight={getRowContentHeight}
      />
    </div>
  );
}
